"use client";

import { Modal } from "./modal";
import { UpgradeButton } from "./upgrade-button";
import { Button } from "./button";
import { Field } from "./field";
import { Input } from "./input";
import { Icon, ArrowRight } from "./icon";
import { PriceLine } from "./upsell-modal";
import type { PaywallPlanPresentation } from "@/lib/billing/paywallPresentation";
import type { PaywallTrigger } from "@/lib/analytics/paywall";
import { paywallCopyForTrigger } from "@/lib/billing/paywallCopy";
import { defaultBrandConfig } from "@/config/brand.default";

/**
 * The generic Pro-upsell modal (MAP-117, D2). ONE component reused by every
 * Pro-gated surface site-wide (visibility, password, exports, API keys,
 * scheduled refresh, folders/tags — D3 through D6 wire their own gates to
 * `useProUpsell()` from `pro-modal-provider.tsx`). The shared structure stays
 * consistent while `copyForPaywallTrigger()` names the exact value the user
 * was trying to unlock.
 *
 * Built on the shared `Modal`, which now owns Escape-to-close, the focus
 * trap, and `aria-modal`/`aria-labelledby` (see modal.tsx) — every Modal
 * consumer gets these for free, not just this one.
 *
 * The heading rendered through Modal's `title` slot ("You found a Pro
 * feature") IS the dialog's accessible name (Modal wires aria-labelledby to
 * it). The plan badge beside it is decorative — marked aria-hidden — so it
 * doesn't get folded into that name; a screen reader announces exactly the
 * heading text.
 */

export function copyForPaywallTrigger(
  trigger?: PaywallTrigger,
  brandName: string = defaultBrandConfig.brandName,
) {
  return paywallCopyForTrigger(trigger, brandName);
}

export interface ProUpsellModalProps {
  open: boolean;
  onClose: () => void;
  /** Full pricing/checkout page the upgrade CTA links to. */
  pricingHref?: string;
  trigger?: PaywallTrigger;
  plan?: PaywallPlanPresentation | null;
  brandName?: string;
  anonymousUpgrade?: {
    email: string;
    onEmailChange: (value: string) => void;
    onSubmit: () => void;
    loading?: boolean;
    error?: string | null;
    signInHref?: string;
  };
}

export function ProUpsellModal({
  open,
  onClose,
  pricingHref = "/pricing/",
  trigger,
  plan,
  brandName = defaultBrandConfig.brandName,
  anonymousUpgrade,
}: ProUpsellModalProps) {
  const copy = copyForPaywallTrigger(trigger, brandName);
  return (
    <Modal
      open={open}
      onClose={onClose}
      className="pz-promodal"
      title={
        <span className="pz-promodal__titlewrap">
          <span className="pz-promodal__eyebrow" aria-hidden="true">
            <span className="pz-promodal__eyebrow-badge">{plan?.name.toUpperCase() ?? "PRO"}</span>
          </span>
          <span className="pz-promodal__heading">{copy.heading}</span>
        </span>
      }
    >
      {copy.body ? <p className="pz-promodal__lede">{copy.body}</p> : null}
      {plan ? (
        <section className="pz-upsell__plan pz-promodal__plan" aria-label="Required plan">
          <div className="pz-upsell__planidentity">
            <span className="pz-upsell__planlabel">Requires</span>
            <strong className="pz-upsell__planname">{plan.name}</strong>
          </div>
          <p className="pz-upsell__planbenefit">{copy.benefit}</p>
          <PriceLine
            price={{
              monthlyCents: plan.monthlyCents,
              annualCents: plan.annualCents,
              currency: plan.currency,
            }}
          />
        </section>
      ) : null}
      {anonymousUpgrade ? (
        <form
          className="pz-promodal__anonymous"
          onSubmit={(event) => {
            event.preventDefault();
            anonymousUpgrade.onSubmit();
          }}
        >
          <div className="pz-promodal__actionpanel">
            <Field
              label="Enter your email to save this map"
              htmlFor="pro-paywall-email"
              required
              error={anonymousUpgrade.error}
              description="We won't email you without your permission."
            >
              <Input
                id="pro-paywall-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={320}
                value={anonymousUpgrade.email}
                error={Boolean(anonymousUpgrade.error)}
                onChange={(event) => anonymousUpgrade.onEmailChange(event.target.value)}
              />
            </Field>
            <Button
              type="submit"
              variant="upgrade"
              size="lg"
              loading={anonymousUpgrade.loading}
              iconTrailing={anonymousUpgrade.loading ? undefined : <Icon icon={ArrowRight} size="sm" />}
            >
              Save Map
            </Button>
          </div>
          {anonymousUpgrade.signInHref ? (
            <a className="pz-promodal__signin" href={anonymousUpgrade.signInHref}>
              Already have an account? Sign in
            </a>
          ) : null}
          <a className="pz-promodal__signin" href={pricingHref}>
            Compare all plans
          </a>
        </form>
      ) : (
        <div className="pz-promodal__actions">
          <UpgradeButton size="lg" planId={plan?.code} cancelPath={pricingHref}>
            Upgrade to {plan?.name ?? "Pro"}
          </UpgradeButton>
          <a className="pz-promodal__signin" href={pricingHref}>
            Compare all plans
          </a>
        </div>
      )}
    </Modal>
  );
}
